import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { z } from 'zod'
import { billSearchSchema, financeSearchSchema } from '@/features/bill/index.ts'
import { orderListSearchSchema, rentalListSearchSchema } from '@/features/order/index.ts'

dayjs.extend(utc)

export const customerDocumentsSearchSchema = z.object({
  kind: z.enum(['rental', 'service', 'bills', 'groups']).optional().catch(undefined),
  rental: rentalListSearchSchema.optional().catch(undefined),
  service: orderListSearchSchema.optional().catch(undefined),
  bills: billSearchSchema.optional().catch(undefined),
  groups: financeSearchSchema.optional().catch(undefined)
})
export type CustomerDocumentsSearch = z.infer<typeof customerDocumentsSearchSchema>

export function getPreviousMonthCreatedTime(now = dayjs().toDate()) {
  const month = dayjs(now).utcOffset(8).subtract(1, 'month')
  return { createdFrom: month.startOf('month').toISOString(), createdTo: month.endOf('month').toISOString() }
}

export function getCustomerDocumentsDefaults(customerId: number) {
  const common = {
    current: 1,
    pageSize: 20,
    sortField: 'createdAt' as const,
    sortOrder: 'desc' as const
  }
  return {
    kind: 'rental' as const,
    rental: rentalListSearchSchema.parse({ ...common, customerId: [customerId] }),
    service: orderListSearchSchema.parse({ ...common, customerId: [customerId] }),
    bills: billSearchSchema.parse({ ...common, customerId, direction: 'receivable', archived: 'active' }),
    groups: financeSearchSchema.parse({ ...common, customerId })
  }
}

export function scopeCustomerDocumentsSearch(customerId: number, search: CustomerDocumentsSearch) {
  const defaults = getCustomerDocumentsDefaults(customerId)
  return {
    kind: search.kind ?? defaults.kind,
    rental: { ...(search.rental ?? defaults.rental), customerId: [customerId] },
    service: { ...(search.service ?? defaults.service), customerId: [customerId] },
    bills: { ...(search.bills ?? defaults.bills), customerId, direction: 'receivable' as const },
    groups: { ...(search.groups ?? defaults.groups), customerId }
  }
}

export function getPreviousMonthBillSearch(customerId: number, now = dayjs().toDate()): CustomerDocumentsSearch {
  return {
    kind: 'bills',
    bills: { ...getCustomerDocumentsDefaults(customerId).bills, ...getPreviousMonthCreatedTime(now), status: ['draft'] }
  }
}

export type ScopedCustomerDocumentsSearch = ReturnType<typeof scopeCustomerDocumentsSearch>

const documentQuerySchemas = {
  rental: rentalListSearchSchema,
  service: orderListSearchSchema,
  bills: billSearchSchema,
  groups: financeSearchSchema
}

/** URL 只表达当前维度；客户和应收方向由页面范围决定。 */
export function toCustomerDocumentsUrl(search: ScopedCustomerDocumentsSearch): Record<string, unknown> {
  const defaults = getCustomerDocumentsDefaults(1)[search.kind]
  const query = Object.fromEntries(
    Object.entries(search[search.kind]).filter(
      ([key, value]) =>
        value !== undefined &&
        key !== 'customerId' &&
        !(search.kind === 'bills' && key === 'direction') &&
        value !== defaults[key as keyof typeof defaults]
    )
  )
  return { kind: search.kind, ...query }
}

export function parseCustomerDocumentsUrl(raw: Record<string, unknown>): CustomerDocumentsSearch {
  if (['createdTime', 'rental', 'service', 'bills', 'groups'].some((key) => Object.hasOwn(raw, key))) {
    return customerDocumentsSearchSchema.parse(raw)
  }
  const kind = customerDocumentsSearchSchema.shape.kind.parse(raw.kind) ?? 'rental'
  return customerDocumentsSearchSchema.parse({
    kind,
    [kind]: documentQuerySchemas[kind].parse(raw)
  })
}

export function validateCustomerDocumentsUrl(raw: Record<string, unknown>): Record<string, unknown> {
  // 空入口仍交给 beforeLoad 恢复缓存；kind 本身即可标识显式的默认/全时段查询。
  if (!Object.keys(raw).length) {
    return {}
  }
  return toCustomerDocumentsUrl(scopeCustomerDocumentsSearch(1, parseCustomerDocumentsUrl(raw)))
}

export function mergeCustomerDocumentsCache(
  customerId: number,
  current: ScopedCustomerDocumentsSearch,
  cached?: CustomerDocumentsSearch
) {
  const previous = scopeCustomerDocumentsSearch(customerId, cached ?? current)
  return scopeCustomerDocumentsSearch(customerId, {
    ...previous,
    kind: current.kind,
    [current.kind]: current[current.kind]
  })
}
